import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    UserGroupIcon, 
    ShieldCheckIcon,
    XMarkIcon,
    ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import axios from '../../axios';
import PermisosModal from './PermisosModal';
import { Table, useTableSort, useTablePagination } from '../UI';

const Roles = () => {
    const [roles, setRoles] = useState([]);
    const [permisosCurrent, setPermisosCurrent] = useState([]);
    const [usuariosCurrent, setUsuariosCurrent] = useState([]);
    const [isModalUsersOpen, setIsModalUsersOpen] = useState(false);
    const [isModalPermisosOpen, setIsModalPermisosOpen] = useState(false);
    const [currentRolName, setCurrentRolName] = useState('');
    const [loader, setLoader] = useState(true);
    const navigate = useNavigate();

    // Hook de sorting - ordena los datos originales
    const { sortedData, sortConfig, handleSort } = useTableSort(roles);
    
    // Hook de paginación - pagina los datos ordenados
    const { 
        paginatedData, 
        page, 
        setPage, 
        itemsPerPage, 
        setItemsPerPage, 
        totalItems 
    } = useTablePagination(sortedData, { page: 1, itemsPerPage: 10 });

    useEffect(() => {
        getAllRoles();
    }, []);

    const getAllRoles = () => {
        axios.get('api/roles')
            .then((res) => {
                setRoles(res.data);
                setLoader(false);
            })
            .catch(e => {
                console.log("ERROR", e);
                setLoader(false);
            });
    };

    const getRoleUsers = (rolName) => {
        let data = { rol_name: rolName };
        axios.post('api/get_rol_users', data)
            .then((res) => {
                console.log("Usuarios", res.data.users);
                setUsuariosCurrent(res.data.users);
            })
            .catch(e => console.log("Error", e));
    };

    const handleLink = (userID) => {
        setIsModalUsersOpen(false);
        navigate(`/admin/usuarios/detail/${userID}`);
    };

    const openModalUsers = (rolName) => {
        setCurrentRolName(rolName);
        getRoleUsers(rolName);
        setIsModalUsersOpen(true);
    };

    const openPermisosModal = (permisos = null) => {
        setPermisosCurrent(permisos);
        setIsModalPermisosOpen(true);
    };

    // Configuración de columnas
    const columns = [
        {
            key: 'icon',
            label: '',
            width: '60px',
            render: () => (
                <img 
                    src="/images/rol5.png" 
                    alt="Rol" 
                    className="h-7 w-7"
                />
            )
        },
        {
            key: 'name',
            label: 'Rol de acceso',
            sortable: true,
        },
        {
            key: 'usuarios',
            label: 'Usuarios',
            render: (_, rol) => (
                <button
                    onClick={() => openModalUsers(rol.name)}
                    className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#0033ff] to-[#11cdef] text-white border border-transparent hover:shadow-[0_0_15px_0_rgba(0,51,255,0.6)] rounded-lg text-sm font-medium transition-all duration-200"
                >
                    <UserGroupIcon className="h-4 w-4" />
                    <span className="whitespace-nowrap">Ver Usuarios</span>
                </button>
            )
        },
        {
            key: 'permisos',
            label: 'Permisos',
            render: (_, rol) => (
                <button
                    onClick={() => openPermisosModal(rol.permissions)}
                    className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#11cdef] to-[#0033ff] text-white border border-transparent hover:shadow-[0_0_15px_0_rgba(17,205,239,0.6)] rounded-lg text-sm font-medium transition-all duration-200"
                >
                    <ShieldCheckIcon className="h-4 w-4" />
                    <span className="whitespace-nowrap">Ver Permisos</span>
                </button>
            )
        },
    ];

    // Configuración de acciones
    const actions = [
        {
            type: 'edit',
            to: (row) => `${row.id}`,
            tooltip: 'Editar',
        },
        {
            type: 'delete',
            onClick: (rol) => {
                if (window.confirm(`¿Deseas eliminar el rol "${rol.name}"?`)) {
                    console.log('Eliminar rol:', rol);
                    // TODO: Implementar eliminación
                }
            },
            tooltip: 'Eliminar',
        },
    ];

    return (
        <>
            {/* Modal de Usuarios */}
            <Transition show={isModalUsersOpen} as={React.Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsModalUsersOpen(false)}>
                    <TransitionChild
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-[#11cdef]/50" />
                    </TransitionChild>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <TransitionChild
                                as={React.Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <DialogPanel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                                    {/* Header del modal */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setIsModalUsersOpen(false)}
                                            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                        >
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                        
                                        <div className="flex items-center gap-4 p-6 border-b border-gray-200">
                                            <div className="flex-shrink-0">
                                                <img 
                                                    src="/images/rol6.png" 
                                                    alt="Rol" 
                                                    className="h-16 w-16 object-cover rounded-lg"
                                                />
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 mb-1">Nombre del rol</p>
                                                <DialogTitle className="text-xl font-bold text-gray-800" style={{ fontSize: "28px!important" }}>
                                                    {currentRolName}
                                                </DialogTitle>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contenido del modal */}
                                    <div className="p-6">
                                        <h6 className="text-lg font-semibold text-gray-700 mb-4">
                                            Listado de usuarios con acceso a este Rol
                                        </h6>
                                        
                                        {/* Tabla de usuarios */}
                                        <div className="overflow-x-auto">
                                            {/* Header de la tabla */}
                                            <div className="hidden md:grid md:grid-cols-12 gap-4 bg-gray-50 px-4 py-3 rounded-t-lg border border-gray-200 font-medium text-sm text-gray-600">
                                                <div className="col-span-1"></div>
                                                <div className="col-span-5">Nombre</div>
                                                <div className="col-span-4">Email</div>
                                                <div className="col-span-2 text-center">Ver Detalle</div>
                                            </div>
                                            
                                            {/* Filas de usuarios */}
                                            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-b-lg">
                                                {usuariosCurrent.length > 0 ? (
                                                    usuariosCurrent.map((usuario, index) => (
                                                        <div 
                                                            key={usuario.id}
                                                            className={`grid grid-cols-12 gap-4 px-4 py-3 hover:bg-[#0033ff]/5 transition-colors ${
                                                                index !== usuariosCurrent.length - 1 ? 'border-b border-gray-100' : ''
                                                            }`}
                                                        >
                                                            <div className="col-span-1 flex items-center">
                                                                <img 
                                                                    src={usuario.image || "/images/default-user.png"} 
                                                                    alt={usuario.name}
                                                                    className="h-8 w-8 rounded-full object-cover"
                                                                />
                                                            </div>
                                                            <div className="col-span-5 flex items-center text-gray-700">
                                                                {usuario.name}
                                                            </div>
                                                            <div className="col-span-4 flex items-center text-gray-600 text-sm">
                                                                {usuario.email}
                                                            </div>
                                                            <div className="col-span-2 flex items-center justify-center">
                                                                <button
                                                                    onClick={() => handleLink(usuario.id)}
                                                                    className="p-2 text-[#0033ff] hover:bg-[#0033ff]/10 rounded-lg transition-colors"
                                                                >
                                                                    <ClipboardDocumentListIcon className="h-5 w-5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-8 text-center text-gray-500">
                                                        No hay usuarios asignados a este rol
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Modal de Permisos */}
            <PermisosModal 
                permisosCurrent={permisosCurrent} 
                isOpen={isModalPermisosOpen}
                onClose={() => setIsModalPermisosOpen(false)}
            />

            {/* Contenido principal */}
            <div className="container-admin-menu">
                <Breadcrumb hasButton={true} hasFilters={false} />
            </div>
            <div className="container-admin-container">
                <div className="container-admin bg-white rounded-lg shadow-md">
                    {/* Banner superior */}
                    <div className="flex items-center gap-4 rounded-t-lg overflow-hidden">
                        <div className="flex-shrink-0">
                            <img 
                                src="/images/rol.jpg" 
                                alt="Roles" 
                                className="h-20 w-auto object-contain"
                            />
                        </div>
                        <div>
                            <h5 className="text-xl font-bold text-gray-800">Roles de usuarios</h5>
                        </div>
                    </div>

                    {/* Tabla de roles */}
                    <div className="p-5">
                        <Table
                            columns={columns}
                            data={paginatedData}
                            actions={actions}
                            loading={loader}
                            striped={true}
                            hoverable={true}
                            emptyMessage="No hay roles registrados"
                            sorting={{
                                field: sortConfig.field,
                                direction: sortConfig.direction,
                                onSort: handleSort
                            }}
                            pagination={{
                                page,
                                itemsPerPage,
                                totalItems,
                                onPageChange: setPage,
                                onItemsPerPageChange: setItemsPerPage
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Roles;